import dayjs, { Dayjs } from "dayjs";
import { RRule } from "rrule";
import { RepeatingCalendarEventDefinition } from "../../models/RepeatingCalendarEventDefinition";

const colors = [
    [null, "#004dcf"],
    [null, "#006b76"],
    [null, "#008b02"],
    [null, "#1273de"],
    [null, "#5300eb"],
    [null, "#b80000"],
    [null, "#bed3f3"],
    [null, "#bedadc"],
    [null, "#c1e1c5"],
    [null, "#c4def6"],
    [null, "#d4c4fb"],
    [null, "#db3e00"],
    [null, "#eb9694"],
    [null, "#fad0c3"],
    [null, "#fccb00"],
    [null, "#fef3bd"],
];

// Given a string, give a consistent unique number that can be used as an index into the colors array.
// This is used to ensure that the same string will always map to the same color.
const getUniqueNumFromString = (hash: string): number => {
    if (hash.length > 0) {
        let total = hash.charCodeAt(0);

        for (let i = 1; i < hash.length; i++) {
            if (i % 2 == 0) {
                total *= hash.charCodeAt(i);
            } else {
                total /= hash.charCodeAt(i);
            }
        }

        return Math.round(total * 1000);
    }

    return 0;
};

export const randomColorFromString = (str: string): string => {
    const color = colors[getUniqueNumFromString(str) % colors.length][1];

    return color ? color : "#FF9E00";
};

// Given a set of 2 times, get the differences in minutes withing 24 hours (effectively the duration of the event, ignoring day changes)
export const minutesDiffWithinDay = (start: Dayjs, end: Dayjs): number => {
    const diff = end.diff(start, "minute");

    const dayInMinutes = 24 * 60;

    const diffInTime = diff % dayInMinutes;

    return Math.abs(diffInTime);
};

export const dayNumToRRuleDay = (dayNum: number): number => {
    // RRule uses 0 for Monday and 6 for Sunday, we want to convert from 0 for Sunday and 6 for Saturday
    if (dayNum === 0) {
        return 6;
    }

    return dayNum - 1;
};

export const dayNumsToRRuleDays = (dayNums: number[]): number[] => {
    return dayNums.map((day) => {
        return dayNumToRRuleDay(day);
    });
};

export const rRuleDayToDayNum = (rRuleDay: number): number => {
    // RRule uses 0 for Monday and 6 for Sunday, we want to convert from 0 for Sunday and 6 for Saturday
    if (rRuleDay === 6) {
        return 0;
    }

    return rRuleDay + 1;
};

export const rRuleDaysToDayNums = (rRuleDays: number[]): number[] => {
    return rRuleDays.map((day) => {
        return rRuleDayToDayNum(day);
    });
};

export const contiunousSequenceFromStartAndEnd = (start: number, end: number): number[] => {
    const days: number[] = [];

    let current = start;

    while (current !== end) {
        days.push(current);
        current = (current + 1) % 7;
    }

    days.push(end);

    return days;
};

// If the list is....
// 0, 1, 2, 3 then the start is 0
// 6, 0, 1, 2, 3 then the start is 6
// 5, 6, 0, 1, 2, 3 then the start is 5
export const firstDayOfContinuousSequence = (days: number[]): number => {
    if (days.length === 0) return 0;
    if (days.length === 7) return 0;

    const daySet = new Set(days);

    // Find the first day where the previous day (mod 7) is NOT in the set
    // That break in continuity is the start of the sequence
    for (const day of days) {
        const prevDay = (day + 6) % 7; // equivalent to (day - 1 + 7) % 7
        if (!daySet.has(prevDay)) {
            return day;
        }
    }

    // All 7 days present — no clear start, return first element
    return days[0];
};

export const lastDayOfContinuousSequence = (days: number[]): number => {
    if (days.length === 0) return 6;
    if (days.length === 7) return 6;

    const daySet = new Set(days);

    // Find the first day where the next day (mod 7) is NOT in the set
    // That break in continuity is the end of the sequence
    for (const day of days) {
        const nextDay = (day + 1) % 7;
        if (!daySet.has(nextDay)) {
            return day;
        }
    }

    // All 7 days present — no clear end, return last element
    return 6;
};

export const workWeekPreviewText = (repeatingCalendarEventDefinition: RepeatingCalendarEventDefinition): string => {
    const deliveryHoursAllDay = repeatingCalendarEventDefinition.duration % (24 * 60) === 0;

    if (deliveryHoursAllDay) {
        const durationInDays = repeatingCalendarEventDefinition.duration / (24 * 60);

        const startDay = repeatingCalendarEventDefinition.startAt || dayjs();

        if (startDay === undefined) {
            return "N/A";
        }

        const startDayName = startDay.format("dddd");
        const endDayName = startDay.add(durationInDays - 1, "day").format("dddd");

        if (startDayName === endDayName) {
            return startDayName + ", All Day";
        }

        return `${startDayName} - ${endDayName}, All Day`;
    }

    const rrule = RRule.fromString(repeatingCalendarEventDefinition.rrule);

    const days = rRuleDaysToDayNums(rrule.options.byweekday as number[]);

    const startDayName = dayjs().day(days[0]).format("dddd");
    const endDayName = dayjs()
        .day(days[days.length - 1])
        .format("dddd");

    const startTime = repeatingCalendarEventDefinition.startAt || dayjs();
    const endTime = startTime.add(repeatingCalendarEventDefinition.duration, "minute");

    const timeString = `${startTime.format("hh:mm A")} - ${endTime.format("hh:mm A")}`;

    if (startDayName === endDayName) {
        return startDayName + ", " + timeString;
    }

    return `${startDayName} - ${endDayName}, ${timeString}`;
};

export const dateRangeForFullCalendarPreview = (currentViewedDay: Dayjs): { startAt: Dayjs; endAt: Dayjs } => {
    // Start with the full "block" that month would render (beginning of week of first of month)
    const startOfMonthBlock = currentViewedDay.startOf("month").startOf("week").startOf("day");

    // Roll back 7 days to include schedules like "24 hours 7 days a week" where the RCE starts midweek and would otherwise be cut off from the calendar view
    const startAt = startOfMonthBlock.subtract(7, "day");

    // Add 6 weeks to full encompass a month view, regardless of number of days for that month
    const endAt = startAt.add(6, "week").endOf("week").endOf("day");

    return { startAt, endAt };
};
